import { Button, Text } from '@chakra-ui/react';
import { ComponentProps } from 'react';
import { FallbackProps } from 'react-error-boundary';
import fonts from 'src/theme/fonts';
import { CacheBusterContextType, useCacheBusterContext } from '../cache-buster/CacheBusterContext';
import ErrorDialog from './ErrorDialog';

interface ErrorDialogFallbackProps extends FallbackProps {
  heading?: ComponentProps<typeof ErrorDialog>['heading'];
  Body?: ComponentProps<typeof ErrorDialog>['Body'];
  Footer?: ComponentProps<typeof ErrorDialog>['Body'];
}

export default function ErrorFallbackDialog({
  error,
  heading,
  resetErrorBoundary,
  Body,
  Footer,
}: ErrorDialogFallbackProps) {
  const cacheBuster = useCacheBusterContext();

  if (cacheBuster.open) {
    return (
      <ErrorFallbackForUpdateError
        cacheBuster={cacheBuster}
        resetErrorBoundary={resetErrorBoundary}
      />
    );
  }

  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <ErrorDialog
      open
      onClose={resetErrorBoundary}
      heading={heading}
      Body={
        Body ?? (
          <Text color="podcastShade500">
            {errorMessage.replaceAll('Error: ', '')}. Sorry about that!
          </Text>
        )
      }
      Footer={Footer}
      errorMessage={errorMessage}
    />
  );
}

interface ErrorFallbackForUpdateErrorProps {
  cacheBuster: CacheBusterContextType;
  resetErrorBoundary: ErrorDialogFallbackProps['resetErrorBoundary'];
}

/**
 * @description
 * Render this component when cache buster dialog is open and the error fallback is triggered
 * This is for the occasional case of out of sync frontend and commander state causing an error
 */
function ErrorFallbackForUpdateError({
  cacheBuster,
  resetErrorBoundary,
}: ErrorFallbackForUpdateErrorProps) {
  return (
    <ErrorDialog
      open
      onClose={() => {
        cacheBuster.onClose();
        resetErrorBoundary();
      }}
      heading="A new version of Alitu is available"
      Body={<Text fontFamily={fonts.bold}>Please update to continue using Alitu</Text>}
      Footer={
        <Button h="48px" w="100%" onClick={cacheBuster.refresh}>
          Update
        </Button>
      }
    />
  );
}
